import { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import {
  Icon,
  IconName,
  Loader,
  TableSelectable as Table,
  ColumnDirection,
  tokens,
  Tooltip,
  Text,
} from '@hotjar/hotsauce';
import { QueryFormatFilter } from '@hotjar/global-filters';
import { RecordingsTable } from './RecordingsTable';
import { css } from '@emotion/react';
import { epochDateTime } from '@hotjar/webapp/lib/time';
import { moment } from '@hotjar/webapp/lib/moment';
import { sanitizeUUID } from '@hotjar/webapp/lib/sanitize';
import { stripDomain, stripProtocol } from '@hotjar/webapp/utils/urls';
import { getMinutesAndSeconds } from '@hotjar/webapp/lib/getMinutesAndSeconds';
import { capitalize } from 'lodash-es';
import type { Columns, Recording } from '../../store/list/types';
import { CountryFlag, COUNTRIES } from '@hotjar/country-lib';
import type { WatchedHotjarFilter } from '../../store/list/types';
import { useTranslation } from 'react-i18next';
import {
  PendingInstallationEmptyState,
  RecordingsEmptyDataState,
} from '@hotjar/webapp/features/Activation/EmptyStates';
import { localeNumber } from '@hotjar/insights-common/lib/number';
import { useFeatureFlag } from '@hotjar/webapp/hooks/useFeatureFlag';
import { OSCell } from './cells/OSCell';
import { BrowserCell } from './cells/BrowserCell';
import { URLCell } from './cells/URLCell';
import { PlayRecordingCell } from './cells/PlayRecordingCell';
import { ScoreCell } from './cells/ScoreCell';
import { MessageForUnknownData } from './common';
import { LabelsCommentsList } from './LabelsCommentsList';
import { RecordingsListEmptyState } from './RecordingsListEmptyState';
import { useTotalHitsUpperBound } from '../../hooks/useTotalHitsUpperBound';
import { formatRecordingsCount } from '../../utils/formatRecordingsCount';
import { MARKS } from '@hotjar/webapp/performance';
import { measureAndLog } from '@hotjar/performance';
import { useNavigation } from '@hotjar/navigation';
import { CommentsByRecordingId, LabelsByRecordingId } from '../../domains/highlights';
import { getRelevanceScoreTranslationKey } from '../RecordingsPage/utils/getRelevanceScoreTranslationKey';
import { scoreLabel } from './utils/getScoreLabel';
import { getEngagementScoreAsBucket, getFrustrationScoreAsBucket } from './utils/calculateScore';
import { LabelsV2CommentsList } from './LabelsV2CommentsList';

import type { Site } from '@hotjar/site';

const countryCodeToName: Record<string, string> = COUNTRIES.reduce(
  (acc: Record<string, string>, country: { value: string; label: string }) => {
    acc[country.value] = country.label;
    return acc;
  },
  {},
);

export type RecordingsListProps = {
  recordings: Recording[];
  onRecordingSelectionChange: (recording: Recording) => void;
  onPageSelectionChange: () => void;
  onPageDeletionCancel: () => void;
  onSelectedRecordingsDelete: () => Promise<boolean>;
  onSelectAllRecordingsAcrossPages: () => void;
  onPlay: (recording: Recording) => void;
  isLoading: boolean;
  onSort: (by: string, reverse: boolean) => void;
  pageSize: number;
  currentPage: number;
  listColumns: Columns;
  commentsByRecordingId: CommentsByRecordingId;
  labelsByRecordingId: LabelsByRecordingId;
  watchedHotjarFilter: WatchedHotjarFilter;
  onEmptyStateButtonClick: () => void;
  onFiltersChangePromptClick: () => void;
  isInstallationPending: boolean;
  isDataPending: boolean;
  totalCount: number;
  onRecordingsChange: () => void;
  jsonFilters: QueryFormatFilter;
  shouldShowFrustrationAndEngagementColumns: boolean;
  site: Site;
};

const sortOptionsForQuery: Record<string, string> = {
  relevance: 'relevance_score',
  user: 'visitor_uuid',
  country: 'country_code',
  pages: 'page_count',
  actions: 'action_count',
  length: 'duration',
  date: 'created',
  device: 'device',
  browser: 'browser',
  operatingSystem: 'os',
  frustration: 'frustration_score',
  engagement: 'engagement_score',
  referrer: 'referrer.raw', // @TODO change to 'referrer' once v2 (ES) is dropped.
  landingPage: 'first_page_path.raw', // @TODO change to 'landing_page_url' once v2 (ES) is dropped.
  exitPage: 'last_page_path.raw', // @TODO change to 'exit_page_url' once v2 (ES) is dropped.
};

/*
  We map the column direction values - desc, asc, none
  to a boolean.
  The boolean is passed to the onSort prop onSortingChange as a 2nd params that represent the reverse value.
  |-> see /playbacks-recordings-list-ctrl.js@onSortingChange

  Map:
  desc -> true
  asc -> false

  `none` will never be mapped, because it's used as a waiting state between user interaction with the columns.
*/
const getMappedSortDirectionBy = (sortDirection: ColumnDirection): boolean =>
  sortDirection === 'desc';

const epochDateShort = (epochTime: number): string => {
  return moment(epochTime * 1000).format('DD MMM, HH:mm');
};

const LoaderContainer = styled.div<{ isLoading: boolean }>`
  display: ${({ isLoading }) => (isLoading ? 'flex' : 'auto')};
  justify-content: ${({ isLoading }) => (isLoading ? 'center' : 'auto')};
  align-content: center;

  min-height: ${({ isLoading }) => (isLoading ? 'calc(100vh - 500px)' : 'auto')};
`;

const TableContainer = styled.div`
  margin: ${tokens.spacer.x3} 0;

  /*-------------------------
      Table styles override
  --------------------------*/
  & .recordings-table td {
    vertical-align: middle;
    padding: ${tokens.spacer.x1} ${tokens.spacer.x2};
  }

  & .recordings-table label {
    margin: 0;
  }

  & .recordings-table th {
    height: 48px;
  }

  & .recordings-table th:first-of-type span {
    line-height: 1;
  }
`;

const OneLiner = styled.span`
  white-space: pre;
`;

const Country = styled.div`
  white-space: nowrap;
  display: flex;
  align-items: center;
`;

const TableHead = ({
  rows,
  listColumns,
  selectedRowsIds,
  selectedSortOption,
  selectedSortDirection,
  shouldShowFrustrationAndEngagementColumns,
  onSort,
  onPageSelectionChange,
  deselectAllRecordingsAcrossPages,
  updateSorting,
  toggleAllRowsSelected,
}: {
  rows: Recording[];
  listColumns: Columns;
  selectedSortOption: string;
  selectedSortDirection: ColumnDirection;
  shouldShowFrustrationAndEngagementColumns: boolean;
  onSort: (by: string, reverse: boolean) => void;
  onPageSelectionChange: () => void;
  selectedRowsIds: Array<number>;
  deselectAllRecordingsAcrossPages: () => void;
  updateSorting: (currOption: string, direction: ColumnDirection) => void;
  toggleAllRowsSelected: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const { topBarHeight: topNavHeight } = useNavigation();

  const getDirectionByOption = useCallback(
    (currOption: string): ColumnDirection =>
      currOption === selectedSortOption ? selectedSortDirection : 'none',
    [selectedSortOption, selectedSortDirection],
  );

  const handleSortFor = useCallback(
    (option) => (direction: ColumnDirection) => {
      onSort(option, getMappedSortDirectionBy(direction));
      updateSorting(option, direction);
    },
    [onSort, updateSorting],
  );

  return (
    <Table.Head size="m">
      <Table.HeadRow
        isSelected={selectedRowsIds.length > 0 && selectedRowsIds.length === rows.length}
        onSelectionChange={() => {
          deselectAllRecordingsAcrossPages();
          toggleAllRowsSelected();
          onPageSelectionChange();
        }}
        stickyOffset={topNavHeight}
      >
        <Table.HeadCell>{/* Play button column */}</Table.HeadCell>
        {listColumns?.comments?.show && (
          <Table.HeadCell>
            <OneLiner>{t('common:recordings.sorting.highlight-action-count')}</OneLiner>
          </Table.HeadCell>
        )}
        {listColumns?.relevance?.show && (
          <Table.HeadCell
            isSortable
            sortDirection={getDirectionByOption(sortOptionsForQuery.relevance)}
            onSort={handleSortFor(sortOptionsForQuery.relevance)}
          >
            <OneLiner>
              <Tooltip text={t('common:recordings.relevance-score.tooltip')} placement="top">
                <span>{t('common:recordings.sorting.relevance-score')}</span>
              </Tooltip>
            </OneLiner>
          </Table.HeadCell>
        )}
        {shouldShowFrustrationAndEngagementColumns && listColumns?.frustration?.show && (
          <Table.HeadCell
            isSortable
            sortDirection={getDirectionByOption(sortOptionsForQuery.frustration)}
            onSort={handleSortFor(sortOptionsForQuery.frustration)}
          >
            <OneLiner>
              <Tooltip text={t('common:recordings.frustration-score.tooltip')} placement="top">
                <span>{t('common:recordings.sorting.frustration-score')}</span>
              </Tooltip>
            </OneLiner>
          </Table.HeadCell>
        )}
        {shouldShowFrustrationAndEngagementColumns && listColumns?.engagement?.show && (
          <Table.HeadCell
            isSortable
            sortDirection={getDirectionByOption(sortOptionsForQuery.engagement)}
            onSort={handleSortFor(sortOptionsForQuery.engagement)}
          >
            <OneLiner>
              <Tooltip text={t('common:recordings.engagement-score.tooltip')} placement="top">
                <span>{t('common:recordings.sorting.engagement-score')}</span>
              </Tooltip>
            </OneLiner>
          </Table.HeadCell>
        )}
        {listColumns?.date?.show && (
          <Table.HeadCell
            isSortable
            sortDirection={getDirectionByOption(sortOptionsForQuery.date)}
            onSort={handleSortFor(sortOptionsForQuery.date)}
          >
            <OneLiner>{t('common:recordings.sorting.created')}</OneLiner>
          </Table.HeadCell>
        )}
        {listColumns?.user?.show && (
          <Table.HeadCell
            isSortable
            sortDirection={getDirectionByOption(sortOptionsForQuery.user)}
            onSort={handleSortFor(sortOptionsForQuery.user)}
          >
            <OneLiner>{t('common:recordings.sorting.visitor-uuid')}</OneLiner>
          </Table.HeadCell>
        )}
        {listColumns?.country?.show && (
          <Table.HeadCell
            isSortable
            sortDirection={getDirectionByOption(sortOptionsForQuery.country)}
            onSort={handleSortFor(sortOptionsForQuery.country)}
          >
            <OneLiner>{t('common:recordings.sorting.country-code')}</OneLiner>
          </Table.HeadCell>
        )}
        {listColumns?.action_count?.show && (
          <Table.HeadCell
            isSortable
            sortDirection={getDirectionByOption(sortOptionsForQuery.actions)}
            onSort={handleSortFor(sortOptionsForQuery.actions)}
          >
            <OneLiner>{t('common:recordings.sorting.action-count')}</OneLiner>
          </Table.HeadCell>
        )}
        {listColumns?.page_count?.show && (
          <Table.HeadCell
            isSortable
            sortDirection={getDirectionByOption(sortOptionsForQuery.pages)}
            onSort={(newDirection) => {
              onSort(sortOptionsForQuery.pages, getMappedSortDirectionBy(newDirection));
              updateSorting(sortOptionsForQuery.pages, newDirection);
            }}
          >
            <OneLiner>{t('common:recordings.sorting.page-count')}</OneLiner>
          </Table.HeadCell>
        )}
        {listColumns?.duration?.show && (
          <Table.HeadCell
            isSortable
            sortDirection={getDirectionByOption(sortOptionsForQuery.length)}
            onSort={handleSortFor(sortOptionsForQuery.length)}
          >
            <OneLiner>{t('common:recordings.sorting.duration')}</OneLiner>
          </Table.HeadCell>
        )}
        {listColumns?.device?.show && (
          <Table.HeadCell
            isSortable
            sortLabel={t('common:recordings.sorting.device')}
            sortDirection={getDirectionByOption(sortOptionsForQuery.device)}
            onSort={handleSortFor(sortOptionsForQuery.device)}
          >
            <OneLiner>
              <Tooltip text={t('common:recordings.sorting.device')} placement="top">
                <Icon icon="device_multiple" size="m" />
              </Tooltip>
            </OneLiner>
          </Table.HeadCell>
        )}
        {listColumns?.browser?.show && (
          <Table.HeadCell
            isSortable
            sortLabel={t('common:recordings.sorting.browser')}
            sortDirection={getDirectionByOption(sortOptionsForQuery.browser)}
            onSort={handleSortFor(sortOptionsForQuery.browser)}
          >
            <OneLiner>
              <Tooltip text={t('common:recordings.sorting.browser')} placement="top">
                <Icon icon="browser" size="m" />
              </Tooltip>
            </OneLiner>
          </Table.HeadCell>
        )}
        {listColumns?.operating_system?.show && (
          <Table.HeadCell
            isSortable
            sortLabel={t('common:recordings.sorting.os')}
            sortDirection={getDirectionByOption(sortOptionsForQuery.operatingSystem)}
            onSort={handleSortFor(sortOptionsForQuery.operatingSystem)}
          >
            <OneLiner>
              <Tooltip text={t('common:recordings.sorting.os')} placement="top">
                <Icon icon="unknown" size="m" />
              </Tooltip>
            </OneLiner>
          </Table.HeadCell>
        )}
        {listColumns?.referrer_url?.show && (
          <Table.HeadCell
            isSortable
            sortDirection={getDirectionByOption(sortOptionsForQuery.referrer)}
            onSort={handleSortFor(sortOptionsForQuery.referrer)}
          >
            <OneLiner>{t('common:recordings.sorting.referrer-raw')}</OneLiner>
          </Table.HeadCell>
        )}
        {listColumns?.landing_url?.show && (
          <Table.HeadCell
            isSortable
            sortDirection={getDirectionByOption(sortOptionsForQuery.landingPage)}
            onSort={handleSortFor(sortOptionsForQuery.landingPage)}
          >
            <OneLiner>{t('common:recordings.sorting.first-page-path-raw')}</OneLiner>
          </Table.HeadCell>
        )}
        {listColumns?.exit_url?.show && (
          <Table.HeadCell
            isSortable
            sortDirection={getDirectionByOption(sortOptionsForQuery.exitPage)}
            onSort={handleSortFor(sortOptionsForQuery.exitPage)}
          >
            <OneLiner>{t('common:recordings.sorting.last-page-path-raw')}</OneLiner>
          </Table.HeadCell>
        )}
      </Table.HeadRow>
    </Table.Head>
  );
};

export const RecordingsList = ({
  recordings,
  onRecordingSelectionChange,
  onPageSelectionChange,
  onPageDeletionCancel,
  onSelectAllRecordingsAcrossPages,
  onSelectedRecordingsDelete,
  onPlay,
  isLoading,
  onSort,
  listColumns,
  commentsByRecordingId,
  labelsByRecordingId,
  watchedHotjarFilter,
  onEmptyStateButtonClick,
  onFiltersChangePromptClick,
  isInstallationPending,
  isDataPending,
  totalCount,
  onRecordingsChange,
  jsonFilters,
  shouldShowFrustrationAndEngagementColumns,
  site,
}: RecordingsListProps): JSX.Element => {
  const isCustomLabelsAllowed = useFeatureFlag('settings.custom-labels');
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [selectedRowsIds, setSelectedRowsIds] = useState<number[]>([]);
  const [selectedSortOption, setSelectedSortOption] = useState<string>('');
  const [selectedSortDirection, setSelectedSortDirection] = useState<ColumnDirection>('none');
  const [areAllRecordingsSelectedAcrossPages, setAreAllRecordingsSelectedAcrossPages] =
    useState(false);
  const { t } = useTranslation();
  const totalHitsUpperBound = useTotalHitsUpperBound();
  const rows = recordings;

  // Get initial sort option from URL - if any
  useEffect(() => {
    if (isLoading) {
      return;
    }

    const currUrl = new URL(window.location.href);
    const initialSortBy = currUrl.searchParams.get('sort_by');

    if (!initialSortBy) {
      return;
    }

    let selectedOption = initialSortBy;
    let selectedDirection: ColumnDirection = 'asc';

    if (initialSortBy.includes('-')) {
      selectedOption = initialSortBy.substr(1);
      selectedDirection = 'desc';
    }

    setSelectedSortOption(selectedOption);
    setSelectedSortDirection(selectedDirection);
  }, [isLoading]);

  useEffect(() => {
    if (initialFetchDone || isLoading) {
      return;
    }

    setInitialFetchDone(true);
    measureAndLog({
      name: MARKS.RECORDINGS_LIST_RENDERED,
      startMark: MARKS.RECORDINGS_LIST_FETCH_TIME,
    });
  }, [isLoading, initialFetchDone]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    onRecordingsChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, recordings]);

  const clearSelection = () => {
    setSelectedRowsIds([]);
  };

  const toggleAllRowsSelected = () => {
    if (selectedRowsIds.length > 0) {
      clearSelection();
    } else {
      selectAllRows();
    }
  };

  const selectAllRows = () => {
    setSelectedRowsIds(rows.map((row: Recording) => row.id));
  };

  const deselectAllRecordingsAcrossPages = () => {
    if (!areAllRecordingsSelectedAcrossPages) {
      return;
    }

    setAreAllRecordingsSelectedAcrossPages(false);
  };

  const toggleRowSelection = (rowId: number) => {
    const selected = [...selectedRowsIds];
    const rowIndex = selectedRowsIds.indexOf(rowId);

    if (rowIndex > -1) {
      selected.splice(rowIndex, 1);
    } else {
      selected.push(rowId);
    }

    setSelectedRowsIds(selected);
  };

  const updateSorting = useCallback(
    (currOption: string, direction: ColumnDirection): void => {
      if (currOption !== selectedSortOption) {
        setSelectedSortOption(currOption);
      }

      setSelectedSortDirection(direction);
    },
    [selectedSortOption],
  );

  if (isInstallationPending) {
    return <PendingInstallationEmptyState />;
  }

  if (isDataPending && !isLoading) {
    return <RecordingsEmptyDataState />;
  }

  if (!isLoading && rows.length === 0) {
    return (
      <RecordingsListEmptyState
        onButtonClick={onEmptyStateButtonClick}
        watchedHotjarFilter={watchedHotjarFilter}
        jsonFilters={jsonFilters}
        onFiltersChangePromptClick={onFiltersChangePromptClick}
        site={site}
      />
    );
  }

  const totalRecordingsSelected = formatRecordingsCount(totalCount, totalHitsUpperBound);
  const noOfSelectedRecordings = areAllRecordingsSelectedAcrossPages
    ? totalRecordingsSelected
    : localeNumber(selectedRowsIds.length);

  const currSelectedRecordingsMessage =
    selectedRowsIds.length === 1
      ? t('common:recordings.list-actions.selected-recording')
      : t('common:recordings.list-actions.selected-recordings', {
          noOfRecordings: noOfSelectedRecordings,
        });

  return (
    <LoaderContainer isLoading={isLoading}>
      <Loader size="xl" isLoading={isLoading}>
        <TableContainer>
          <Table
            className="recordings-table"
            caption="Recordings Table"
            tableName="recordingsTable"
            numberRowsSelected={selectedRowsIds.length}
            customTable={<RecordingsTable />}
            leftActionProps={{
              text: `Select all ${totalRecordingsSelected} recordings`,
              kind: 'secondary',
              isModalClosingOnClick: false,
              isHidden: areAllRecordingsSelectedAcrossPages,
              onClick: async () => {
                onSelectAllRecordingsAcrossPages();
                selectAllRows();
                setAreAllRecordingsSelectedAcrossPages(true);
              },
            }}
            rightActionProps={{
              text: 'Delete',
              icon: 'trash',
              kind: 'danger_secondary',
              onClick: async () => {
                const deleted: boolean = await onSelectedRecordingsDelete();

                if (deleted) {
                  clearSelection();
                }
              },
            }}
            onClose={() => {
              clearSelection();
              onPageDeletionCancel();
              deselectAllRecordingsAcrossPages();
            }}
            actionMessage={currSelectedRecordingsMessage}
          >
            <TableHead
              rows={rows}
              listColumns={listColumns}
              selectedRowsIds={selectedRowsIds}
              selectedSortOption={selectedSortOption}
              selectedSortDirection={selectedSortDirection}
              shouldShowFrustrationAndEngagementColumns={shouldShowFrustrationAndEngagementColumns}
              onSort={onSort}
              onPageSelectionChange={onPageSelectionChange}
              deselectAllRecordingsAcrossPages={deselectAllRecordingsAcrossPages}
              updateSorting={updateSorting}
              toggleAllRowsSelected={toggleAllRowsSelected}
            />
            <Table.Body>
              {rows.map((row: Recording) => {
                const deviceTooltipText = `${capitalize(row.device)} (${row.window_size[0]} X ${
                  row.window_size[1]
                })`;
                const deviceIconName = `device_${row.device}` as IconName;
                const frustrationScore = getFrustrationScoreAsBucket(row.frustration_score);
                const engagementScore = getEngagementScoreAsBucket(row.relevance_score);

                return (
                  <Table.Row
                    key={row.id}
                    onSelectionChange={() => {
                      toggleRowSelection(row.id);
                      onRecordingSelectionChange(row);
                      deselectAllRecordingsAcrossPages();
                    }}
                    isSelected={selectedRowsIds.indexOf(row.id) > -1}
                  >
                    {/* Play button */}
                    <PlayRecordingCell recording={row} onPlay={onPlay} />

                    {/* Highlights */}
                    {listColumns?.comments?.show && (
                      <Table.Cell>
                        {!isCustomLabelsAllowed ? (
                          <LabelsCommentsList
                            labels={labelsByRecordingId[row.id] ?? []}
                            comments={commentsByRecordingId[row.id] ?? []}
                            size="l"
                            slotsNumber={3}
                          />
                        ) : (
                          <LabelsV2CommentsList
                            labels={labelsByRecordingId[row.id] ?? []}
                            comments={commentsByRecordingId[row.id] ?? []}
                            size="m"
                            slotsNumber={3}
                          />
                        )}
                      </Table.Cell>
                    )}

                    {/* Relevance */}
                    {listColumns?.relevance?.show && (
                      <Table.Cell>
                        {t(getRelevanceScoreTranslationKey(row.relevance_score))}
                      </Table.Cell>
                    )}

                    {/* Frustration */}
                    {shouldShowFrustrationAndEngagementColumns &&
                      listColumns?.frustration?.show && (
                        <Table.Cell>
                          {frustrationScore !== null && (
                            <ScoreCell
                              score={frustrationScore}
                              colorActive={tokens.BRAND_COLORS.magenta_800}
                              colorInactive={tokens.BRAND_COLORS.magenta_400}
                              trackingLabel="Frustration"
                              title={t(
                                `common:recordings.frustration-score.${scoreLabel[frustrationScore]}.title`,
                              )}
                              text={t(
                                `common:recordings.frustration-score.${scoreLabel[frustrationScore]}.text`,
                              )}
                            />
                          )}
                        </Table.Cell>
                      )}

                    {/* Engagement */}
                    {shouldShowFrustrationAndEngagementColumns && listColumns?.engagement?.show && (
                      <Table.Cell>
                        {engagementScore !== null && (
                          <ScoreCell
                            score={engagementScore}
                            colorActive={tokens.BRAND_COLORS.cobalt_900}
                            colorInactive={tokens.BRAND_COLORS.cobalt_400}
                            trackingLabel="Engagement"
                            title={t(
                              `common:recordings.engagement-score.${scoreLabel[engagementScore]}.title`,
                            )}
                            text={t(
                              `common:recordings.engagement-score.${scoreLabel[engagementScore]}.text`,
                            )}
                          />
                        )}
                      </Table.Cell>
                    )}

                    {/* Time */}
                    {listColumns?.date?.show && (
                      <Table.Cell>
                        <Tooltip
                          text={epochDateTime(row.created_epoch_time)}
                          placement="top"
                          appendTo="parent"
                        >
                          <OneLiner>{epochDateShort(row.created_epoch_time)}</OneLiner>
                        </Tooltip>
                      </Table.Cell>
                    )}

                    {/* Visitor ID */}
                    {listColumns?.user?.show && (
                      <Table.Cell>
                        {row.visitor_uuid ? (
                          <Tooltip
                            text={t('common:recordings.list.uuid.tooltip')}
                            placement="top"
                            appendTo="parent"
                          >
                            <span>
                              {sanitizeUUID(row.visitor_uuid)}
                              {row.is_first_seen && <Text textColor="text_light">&nbsp;(new)</Text>}
                            </span>
                          </Tooltip>
                        ) : (
                          '-'
                        )}
                      </Table.Cell>
                    )}

                    {/* Country */}
                    {listColumns?.country?.show && (
                      <Table.Cell>
                        <Country>
                          <CountryFlag
                            css={css`
                              margin-right: ${tokens.spacer.x1};
                            `}
                            code={row.country_code}
                          />
                          {row.country_code ? (
                            countryCodeToName[row.country_code]
                          ) : (
                            <MessageForUnknownData>Unknown</MessageForUnknownData>
                          )}
                        </Country>
                      </Table.Cell>
                    )}

                    {/* Action count */}
                    {listColumns?.action_count?.show && <Table.Cell>{row.action_count}</Table.Cell>}

                    {/* Page count */}
                    {listColumns?.page_count?.show && <Table.Cell>{row.page_count}</Table.Cell>}

                    {/* Duration */}
                    {listColumns?.duration?.show && (
                      <Table.Cell>{getMinutesAndSeconds(row.duration)}</Table.Cell>
                    )}

                    {/* Device */}
                    {listColumns?.device?.show && (
                      <Table.Cell>
                        <Tooltip text={deviceTooltipText} placement="top" appendTo="parent">
                          <Icon icon={deviceIconName} size="m" />
                        </Tooltip>
                      </Table.Cell>
                    )}

                    {/* Browser */}
                    {listColumns?.browser?.show && <BrowserCell browser={row.browser} />}

                    {/* OS */}
                    {listColumns?.operating_system?.show && <OSCell os={row.os} />}

                    {/* Referrer URL */}
                    {listColumns?.referrer_url?.show && (
                      <URLCell
                        url={row.referrer}
                        formatUrlFn={stripProtocol}
                        copyActionDescription="Copy referrer URL"
                      />
                    )}

                    {/* Landing Page URL */}
                    {listColumns?.landing_url?.show && (
                      <URLCell
                        url={row.landing_page_url}
                        formatUrlFn={stripDomain}
                        copyActionDescription="Copy landing page URL"
                      />
                    )}

                    {/* Exit Page URL */}
                    {listColumns?.exit_url?.show && (
                      <URLCell
                        url={row.exit_page_url}
                        formatUrlFn={stripDomain}
                        copyActionDescription="Copy exit page URL"
                      />
                    )}
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </TableContainer>
      </Loader>
    </LoaderContainer>
  );
};
